import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

export function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => element.offsetParent !== null || element === document.activeElement,
  );
}

/** Active traps, innermost last: only the topmost one handles Tab (dialog with an open popover inside). */
const traps: symbol[] = [];

export interface FocusTrapOptions {
  /** Element to focus when the trap activates; default: `[data-autofocus]`, then the first focusable, then the container. */
  initialFocus?: RefObject<HTMLElement | null> | 'first' | 'container' | 'none' | undefined;
  /** Give focus back to the previously focused element when the trap deactivates (default true). */
  returnFocus?: boolean | undefined;
}

/**
 * Keeps Tab / Shift+Tab inside `ref` while `active` (dialogs, sheets, popovers) and restores the
 * previous focus afterwards. The container should carry `tabindex="-1"` so it can take focus itself.
 * The previously focused element is captured during the activating render, before any child effect
 * (such as a menu's own autofocus) moves the focus; the key listener lives on the document so a
 * container that is re-created (portal target change) keeps working.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  options: FocusTrapOptions = {},
): void {
  const { initialFocus, returnFocus = true } = options;
  const previousRef = useRef<HTMLElement | null>(null);
  const wasActive = useRef(false);
  if (active && !wasActive.current) {
    previousRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  wasActive.current = active;

  useEffect(() => {
    if (!active) return;
    const previous = previousRef.current;
    const token = Symbol('trap');
    traps.push(token);

    const frame = requestAnimationFrame(() => {
      const container = ref.current;
      if (!container || initialFocus === 'none') return;
      let target: HTMLElement | null = null;
      if (initialFocus === 'container') target = container;
      else if (initialFocus && initialFocus !== 'first') target = initialFocus.current;
      if (!target) target = container.querySelector<HTMLElement>('[data-autofocus]');
      if (!target) target = focusableElements(container)[0] ?? container;
      target.focus({ preventScroll: true });
    });

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab' || event.defaultPrevented) return;
      if (traps[traps.length - 1] !== token) return;
      const container = ref.current;
      if (!container) return;
      const list = focusableElements(container);
      const current = document.activeElement;
      if (list.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }
      const first = list[0] as HTMLElement;
      const last = list[list.length - 1] as HTMLElement;
      if (!container.contains(current)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && current === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown, true);
      const index = traps.indexOf(token);
      if (index >= 0) traps.splice(index, 1);
      if (returnFocus && previous && previous.isConnected) previous.focus({ preventScroll: true });
    };
  }, [active, initialFocus, ref, returnFocus]);
}
